import { useEffect, useRef, useState } from "react";
import { Tool } from "../../components/fileEditor/fileEditor";

type Point = { x: number; y: number };

export function usePanZoom(
  viewportRef: React.MutableRefObject<HTMLDivElement | null>,
  tool: Tool,
) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });

  const pointers = useRef<Map<number, Point>>(new Map());
  const dragging = useRef(false);

  const lastMid = useRef<Point | null>(null);
  const lastPinchDistance = useRef<number | null>(null);

  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));

  const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

  // Convert client coords to viewport-local coords (important for "zoom about point")
  const toLocal = (clientX: number, clientY: number): Point => {
    const el = viewportRef.current;
    if (!el) return { x: clientX, y: clientY };
    const rect = el.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const el = e.currentTarget as HTMLElement;

    // PC: middle mouse pan
    if (e.pointerType === "mouse" && e.button === 1) {
      e.preventDefault();
      el.setPointerCapture(e.pointerId);

      dragging.current = true;
      lastMid.current = toLocal(e.clientX, e.clientY);
      return;
    }

    // Tablet: only when tool is hand, allow 2-finger pan/zoom
    if (e.pointerType === "touch") {
      if (tool !== "hand") return;

      el.setPointerCapture(e.pointerId);
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.current.size === 2) {
        dragging.current = true;

        const [a, b] = [...pointers.current.values()];
        const midLocal = toLocal((a.x + b.x) / 2, (a.y + b.y) / 2);

        lastMid.current = midLocal;
        lastPinchDistance.current = dist(a, b);
      }
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    // PC: middle mouse panning
    if (e.pointerType === "mouse") {
      if (!dragging.current || !lastMid.current) return;

      const cur = toLocal(e.clientX, e.clientY);
      const dx = cur.x - lastMid.current.x;
      const dy = cur.y - lastMid.current.y;
      lastMid.current = cur;

      setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
      return;
    }

    // Tablet: 2-finger pan + pinch zoom (only in hand tool)
    if (e.pointerType === "touch") {
      if (tool !== "hand") return;
      if (!pointers.current.has(e.pointerId)) return;

      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.current.size !== 2) return;

      const [a, b] = [...pointers.current.values()];

      const midLocal = toLocal((a.x + b.x) / 2, (a.y + b.y) / 2);

      // 1) PAN by midpoint delta
      if (dragging.current && lastMid.current) {
        const dx = midLocal.x - lastMid.current.x;
        const dy = midLocal.y - lastMid.current.y;
        if (dx !== 0 || dy !== 0) {
          setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
        }
      }
      lastMid.current = midLocal;

      // 2) ZOOM about midpoint
      const d = dist(a, b);
      if (lastPinchDistance.current != null) {
        const ratio = d / lastPinchDistance.current;

        setZoom((prevZoom) => {
          const nextZoom = clamp(prevZoom * ratio, 0.25, 4);
          const scale = nextZoom / prevZoom;

          // keep pinch midpoint stable under fingers
          setPan((p) => ({
            x: midLocal.x - (midLocal.x - p.x) * scale,
            y: midLocal.y - (midLocal.y - p.y) * scale,
          }));

          return nextZoom;
        });
      }
      lastPinchDistance.current = d;
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);

    if (e.pointerType === "mouse") {
      dragging.current = false;
      lastMid.current = null;
      return;
    }

    if (e.pointerType === "touch") {
      // End gesture when fewer than 2 fingers
      if (pointers.current.size < 2) {
        dragging.current = false;
        lastMid.current = null;
        lastPinchDistance.current = null;
      }
    }
  };

  const reset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // PC: Ctrl+wheel zoom about cursor (keep as-is behavior)
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      const el = viewportRef.current;
      if (!el || !e.ctrlKey) return;

      const rect = el.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (!inside) return;

      e.preventDefault();

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;

      setZoom((prev) => {
        const next = clamp(prev * factor, 0.25, 4);

        setPan((p) => {
          const scale = next / prev;
          return {
            x: mouseX - (mouseX - p.x) * scale,
            y: mouseY - (mouseY - p.y) * scale,
          };
        });

        return next;
      });
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [viewportRef]);

  return { zoom, pan, onPointerDown, onPointerMove, onPointerUp, reset };
}